import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '@client/store';
import { Utils, AuthCleanup } from '@client/utility';
import { User } from '@supabase/supabase-js';

export const useAccountAPI = () => {
  const [languages, setLanguages] = useState<{ code: string; name: string }[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();
  
  const setIsLogin = useUserStore((state) => state.setIsLogin);
  const clearAllUserData = useUserStore((state) => state.clearAllUserData);
  const setUserUUID = useUserStore((state) => state.setUserUUID);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await fetch('/Languages/Languages.json');
        const data = await response.json();
        setLanguages(data.languages || []);
      } catch (error) {
        console.error('Error fetching languages:', error);
      }
    };

    const fetchUser = async () => {
      const { data: { session } } = await Utils.db.client.auth.getSession();
      
      if (!session) {
        setUser(null);
        setIsLogin(false);
        setUserUUID(null);
        return;
      }

      const { data: userData, error: userError } = await Utils.db.client.auth.getUser();
      
      if (userError || !userData.user) {
        setUser(null);
        setIsLogin(false);
        setUserUUID(null);
        await Utils.db.client.auth.signOut({ scope: 'local' });
      } else {
        setUser(userData.user);
        setUserUUID(userData.user.id);
        setIsLogin(true);
      }
    };

    fetchLanguages();
    fetchUser();

    const { data: { subscription } } = Utils.db.client.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_OUT' || !session) {
          setUser(null);
          setIsLogin(false);
          setUserUUID(null);
        } else if (session?.user) {
          setUser(session.user);
          setUserUUID(session.user.id);
          setIsLogin(true);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [setIsLogin, setUserUUID]);

  const toggleModal = () => setIsModalOpen((prev) => !prev);

  const confirmLogOut = async () => {
    try {
      await Utils.post('/api/LogOut');
      await AuthCleanup();
      await Utils.db.client.auth.signOut({ scope: 'global' });
      
      clearAllUserData();
      setUser(null);
      setIsLogin(false);
      setUserUUID(null);
      
      window.location.href = '/';
    } catch (err) {
      console.error('Error during logout:', err);
      clearAllUserData();
      setUser(null);
      setIsLogin(false);
      setUserUUID(null);
      window.location.href = '/';
    }
  };

  const confirmDeleteAccount = async () => {
    if (!user?.id) return;

    try {
      await Utils.db.update({
        tables: 'user_data',
        values: { is_deleted: true },
        match: { user_uuid: user.id },
      });
      await confirmLogOut();
    } catch (error) {
      console.error('Error deleting account:', error);
      await confirmLogOut();
    }
  };

  return {
    languages,
    isModalOpen,
    user,
    toggleModal,
    confirmLogOut,
    confirmDeleteAccount,
  };
};